/**
 * QUICK ADD — one English sentence into the pass editor (resident-portal RP-4.4).
 *
 *   parseQuickAdd('My cleaner Rosa comes every Tuesday 9-1 until Dec 31', '2026-09-29')
 *     -> category 'cleaner', first_name 'Rosa', weekly on [2] from 2026-09-29,
 *        09:00–13:00, valid_until 2026-12-31
 *
 * IT ONLY PRE-FILLS. Nothing here saves, sends or decides: the result fills the
 * editor's inputs, the resident sees and confirms every one, and the server's
 * own validation (lobbero_be/src/passes/spec.js) still judges what is sent.
 * What the parser did not understand comes back as `leftovers` — for "I didn't
 * understand: …" — rather than being guessed at.
 *
 * DETERMINISTIC ON PURPOSE. No model, no clock, no network. `today` is the
 * building's calendar date, passed in; every date is a whole calendar date
 * worked through pass-schedule's helpers (never `new Date('YYYY-MM-DD')`, which
 * lands on the previous day west of Greenwich). The same sentence and the same
 * `today` read the same on every device. `zone` is accepted for callers that
 * hold the building's zone, and deliberately unused: `today` already IS the
 * building's date, so there is nothing left to convert.
 *
 * HOW IT READS. The text is capped at QUICK_ADD_MAX characters FIRST, then read
 * as plain text — markup is never interpreted, "<b>" is punctuation and a word.
 * Unicode dashes become '-' one for one, so every index still points into the
 * capped text. Rules run in a fixed order and each CONSUMES what it matched, so
 * no later rule reads it again:
 *
 *   1. validity    until / through / to <date>, until <weekday>, until end of
 *                  year, for n weeks|months, from / starting <date|today|tomorrow>
 *   2. dates       Dec 31, 31 Dec, December 31st, 31 December 2026, 2026-12-31,
 *                  31/12; today, tonight, tomorrow. BEFORE times, so no time rule
 *                  ever reads "12-31" out of a date.
 *   3. times       9-1, 9am-1pm, 09:00-13:00, from 9 to 1, between 9 and 1,
 *                  10-noon; mornings / afternoons / evenings / nights; all day
 *   4. repetition  every other / second week, every 2|3|4 weeks, fortnightly,
 *                  biweekly; every week, weekly
 *   5. days        this / next weekend; weekdays, weekends, daily, every day;
 *                  Tuesday, Tuesdays, every Tuesday, Mon-Fri, Monday to Friday,
 *                  Mon, Wed and Fri
 *   6. category    cleaner, nurse, dog walker, plumber, mum, friend, teacher, …
 *   7. name        a run of 1–3 Capitalised words nothing else took — only when
 *                  the sentence leaves no doubt which one (see below)
 *
 * THE DECISIONS WORTH KNOWING
 *   * Weekdays mean what the backend IQ reader means: a bare weekday, "on
 *     Friday" and "this Friday" are the NEAREST such day from today on — today
 *     counts; "next Friday" is the first such day AFTER today, so today + 7 when
 *     today is a Friday.
 *   * One weekday and no repetition cue is ONE visit ("Mum visiting Saturday").
 *     The cues: every / each, a plural (Tuesdays, mornings), two or more days, a
 *     range, weekdays / weekends / daily, an interval, "weekly" — and an end:
 *     "Tuesday until Dec 31" repeats. "this" / "next" always mean one visit.
 *   * A one-time visit has no end. valid_until is left out (the editor sends it
 *     for weekly passes only), and an "until …" / "for n weeks" said alongside
 *     one goes back as a leftover instead of being quietly dropped.
 *   * A repeating pass starts today unless a start is said ("from Oct 6", or a
 *     lone date: "Tuesdays, Oct 6 to Dec 31"). With no end, valid_until is left
 *     unset for the editor to ask. "for n weeks|months" counts from that start
 *     and valid_until is the LAST day of it (it is inclusive): 7n − 1 days on,
 *     or n calendar months less a day. "Every Tuesday for 6 weeks" typed on a
 *     Tuesday is six Tuesdays, not seven.
 *   * A RANGE MEANS THAT RANGE. "this weekend" is the Saturday and Sunday of
 *     this week (weeks start on Monday, as the backend reads them) and nothing
 *     after; "next weekend" is the one after. A bare "weekends" / "every
 *     weekend" still repeats. "until Sunday" ends on the coming Sunday — counted
 *     from the start — and, when no visiting day is said ("staying until
 *     Sunday"), means every day until then. "Monday until Friday" stays a run
 *     of days.
 *   * "Every other Tuesday" starts on the COMING Tuesday: with a 2–4 week
 *     interval the start moves to the first named day on or after it, because
 *     the weeks are counted from the start's own week (owner decision RP-D22).
 *   * Bare hours read the way people say them: an end before the start is the
 *     afternoon while the start is not (9-1 = 09:00–13:00, 12-1 = 12:00–13:00),
 *     a range inside 1–11 that starts at 1–5 is the afternoon (2-5 =
 *     14:00–17:00, 4-4:30 = 16:00–16:30 — nobody is expected at 2 in the night
 *     unless they write 02:00), a start past noon stays overnight (22-6 =
 *     22:00–06:00), and an end written 24-hour style (02:00) is taken as
 *     written. A pm start lends its pm to a bare end that is later (6pm-10 =
 *     18:00–22:00); a bare 12 after it is midnight (9pm-12 = 21:00–00:00);
 *     anything else is the next morning (10pm-2 = 22:00–02:00). am/pm always
 *     wins.
 *   * Words: morning 08:00–12:00, afternoon 12:00–17:00, evening 17:00–21:00,
 *     night 22:00–06:00 (overnight; the engine lets a window cross midnight).
 *     An explicit range beats a word. "all day" = no window (both null).
 *   * A date without a year is this year's, or next year's once this year's has
 *     gone. 31/12 and 12/31 are read (the part over 12 is the day); 3/4 could be
 *     either and is left for the form. Feb 30 is not a date.
 *   * A name is only read from Capitalised words; an all-lower-case "maria" is a
 *     leftover, never a guess. A capitalised job word straight after a name is a
 *     surname ("Sam Walker"), not a category.
 *   * A NAME ONLY WHEN IT IS ONE (owner decision RP-D21). Phone keyboards
 *     capitalise the first word, so a capital at the start of a sentence proves
 *     nothing. Never a name: a role or a thing ("Piano teacher", "Delivery",
 *     "Lunch"), the word in front of one ("Amazon delivery"), and the word after
 *     "from" ("from Amazon"). When the first word and a later Capitalised word
 *     could both be the visitor, the one right after a role wins ("Yoga with my
 *     teacher Anna"); with no such sign the name is LEFT EMPTY and both words
 *     are handed back ("Maria and Tom", "Rosa until Christmas").
 *   * "this", "until" and "till" are not filler: when no rule used them they
 *     come back as leftovers, so a range that was not understood is said.
 *   * The first answer for a field wins; a later, different one ("… until Jan 5"
 *     after "until Dec 31") is a leftover. Nothing is silently dropped. One
 *     exception: a possessive family word in front of another kind of visitor
 *     ("my mum's carer", "my son's tutor") describes whose visitor it is, and
 *     gives way.
 */
import { addDays, addMonths, daysBetween, firstDayOnOrAfter, isRealDate } from './pass-schedule';
import type {
  PassCategory,
  PassScheduleKind,
  PassSpecInput,
} from '../interfaces/visitor-pass';

/** The most of a sentence that is read; the rest is cut off BEFORE parsing. */
export const QUICK_ADD_MAX = 300;

/** The editor field a span filled; 'window' is window_start + window_end. */
export type QuickAddField =
  | Extract<
      keyof PassSpecInput,
      | 'first_name'
      | 'last_name'
      | 'category'
      | 'schedule_kind'
      | 'valid_from'
      | 'valid_until'
      | 'days'
      | 'interval_weeks'
    >
  | 'window';

export interface QuickAddSpan {
  field: QuickAddField;
  /** [start, end) in the capped text. */
  start: number;
  end: number;
  text: string;
}

export interface QuickAddResult {
  /** Only what was understood. */
  fields: Partial<PassSpecInput>;
  /** What was read, and into which field, in input order. */
  matched: QuickAddSpan[];
  /** Words and phrases not understood, in input order, each once. */
  leftovers: string[];
}

/* ─────────────────────────────  Vocabulary  ───────────────────────────── */

const DAY_WORD = String.raw`mon(?:days?)?|tue(?:s(?:days?)?)?|wed(?:s|nesdays?)?|thu(?:r(?:s(?:days?)?)?)?|fri(?:days?)?|sat(?:urdays?)?|sun(?:days?)?`;
const MONTH = String.raw`jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?`;
const ORD = String.raw`(?:st|nd|rd|th)?`;
const DATE = [
  String.raw`\d{4}-\d{1,2}-\d{1,2}`,
  String.raw`(?:the\s+)?\d{1,2}${ORD}\s+(?:of\s+)?(?:${MONTH})\.?(?:,?\s+\d{4})?`,
  String.raw`(?:${MONTH})\.?\s+(?:the\s+)?\d{1,2}${ORD}(?:,?\s+\d{4})?`,
  String.raw`\d{1,2}/\d{1,2}(?:/(?:\d{4}|\d{2}))?`,
].join('|');
const CLOCK = String.raw`(?:\d{1,2}(?:[:.]\d{2})?(?:\s*[ap]\.?m\.?)?|noon|midday|midnight)`;
const UNTIL = String.raw`(?:valid\s+)?(?:until|till|til|through|thru|to|up\s+to|ending(?:\s+on)?|ends(?:\s+on)?|expires(?:\s+on)?)`;

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const MONTH_KEYS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
];
const EVERY_DAY = [1, 2, 3, 4, 5, 6, 7];

const CATEGORY_WORDS: Array<[PassCategory, string[]]> = [
  ['cleaner', ['cleaning lady', 'cleaner', 'housekeeper', 'maid']],
  [
    'carer',
    [
      'care worker',
      'care giver',
      'support worker',
      'caregiver',
      'carer',
      'nurse',
      'physiotherapist',
      'physio',
    ],
  ],
  ['babysitter', ['baby sitter', 'babysitter', 'childminder', 'nanny', 'sitter', 'au pair']],
  ['dog_walker', ['dog walker', 'dog sitter', 'pet sitter', 'walker']],
  [
    'contractor',
    [
      'plumber',
      'electrician',
      'contractor',
      'handyman',
      'painter',
      'builder',
      'technician',
      'repairman',
      'decorator',
      'carpenter',
      'locksmith',
      'engineer',
      'gardener',
    ],
  ],
  [
    'family',
    [
      'mother in law',
      'father in law',
      'sister in law',
      'brother in law',
      'son in law',
      'daughter in law',
      'grandmother',
      'grandfather',
      'granddaughter',
      'grandson',
      'grandma',
      'grandpa',
      'granny',
      'grandad',
      'granddad',
      'mum',
      'mom',
      'mummy',
      'mommy',
      'mother',
      'father',
      'dad',
      'daddy',
      'parents',
      'sister',
      'brother',
      'son',
      'daughter',
      'aunt',
      'auntie',
      'aunty',
      'uncle',
      'cousin',
      'niece',
      'nephew',
      'nan',
      'nana',
      'family',
      'partner',
      'husband',
      'wife',
    ],
  ],
  ['friend', ['girlfriend', 'boyfriend', 'friends', 'friend']],
  // People a sentence names by what they do, with no category of their own:
  // "Piano teacher Anna" is Anna, Other.
  [
    'other',
    [
      'personal trainer',
      'teacher',
      'tutor',
      'instructor',
      'coach',
      'trainer',
      'therapist',
      'hairdresser',
    ],
  ],
];

/**
 * Roles that are said with their subject in front: "piano teacher", "French
 * tutor", "yoga instructor". The word before one belongs to the role — it is
 * never the visitor's name — and none of these is a surname.
 */
const SUBJECT_ROLES = new Set(['teacher', 'tutor', 'instructor', 'coach', 'trainer']);

/**
 * Never a person's name, and not a kind of visitor the form has: what is being
 * brought, who brings it, or the occasion. Left for the reader as "not
 * understood" — and the word in front of one ("Amazon delivery", "Birthday
 * party") is not a name either. Kept beside the category words above: together
 * they are every noun this file knows.
 */
const NOT_A_PERSON = new Set(
  `delivery deliveries courier driver parcel parcels package packages order orders
   groceries shopping takeaway food taxi cab lunch dinner brunch breakfast supper coffee
   tea drinks party playdate sleepover meeting appointment viewing inspection lesson
   lessons class classes piano guitar violin music maths math yoga pilates massage
   repair repairs service maintenance installation survey quote estimate`.split(/\s+/)
);

/** Keyed by the phrase with its spaces removed ("dogwalker"). */
const CATEGORY_OF = new Map<string, PassCategory>(
  CATEGORY_WORDS.flatMap(([category, words]) =>
    words.map(word => [word.replace(/ /g, ''), category] as [string, PassCategory])
  )
);

/** Never a name, never reported as not understood. */
const FILLER = new Set(
  `a an the my our his her their your that these those i me we us you he him she
   it its they them is are was were be been being will would can could might should
   shall do does comes come coming came visit visits visiting visitor visitors arrive
   arrives arriving stay stays staying drop drops dropping stop stops stopping over by
   around about here there at on in from to and or through thru every
   each with for of as also please pass add new expect expecting expected allow let
   lets guest guests usually normally always just only then between starting start
   starts valid via like who which s o'clock hi hello hey mr mrs ms miss dr`.split(/\s+/)
);

/**
 * Schedule words: never a name, but a leftover when nothing used them. "this",
 * "until" and "till" are here, not in FILLER: a range a rule did not read
 * ("until further notice", "this week") is said, not swallowed.
 */
const KEYWORDS = new Set(
  `this until till til
   today tonight tomorrow next other second week weeks month months year years day days
   daily weekly weekday weekdays weekend weekends fortnight fortnightly biweekly noon
   midday midnight morning mornings afternoon afternoons evening evenings night nights
   overnight all any anytime end am pm`.split(/\s+/)
);

/** Month words that are also given names, and so may stand alone as one. */
const NAME_MONTHS = new Set(['jan', 'april', 'may', 'june', 'august']);

const PERIODS: Record<string, [string, string]> = {
  m: ['08:00', '12:00'],
  a: ['12:00', '17:00'],
  e: ['17:00', '21:00'],
  n: ['22:00', '06:00'],
  o: ['22:00', '06:00'],
};

/* ──────────────────────────────  The rules  ───────────────────────────── */

/**
 * A rule's regex: `body` as a whole word, never glued to a letter or digit, and
 * never starting just after a '-', ':', '/' or '.' — so "12-31" out of
 * "2026-12-31" or the "4" of "3/4" is never read on its own. Group 1 is the
 * character before, group 2 the match, groups 3+ the body's own.
 */
const rx = (body: string): RegExp =>
  new RegExp(
    String.raw`(^|[^\p{L}\p{N}\-:/.])(` + body + String.raw`)(?![\p{L}\p{N}]|[/:.\-]\d)`,
    'giu'
  );

const RE_END_OF_YEAR = rx(
  String.raw`${UNTIL}\s+(?:the\s+)?(?:end\s+of\s+(?:the\s+)?year|year[\s\-]end)|for\s+the\s+rest\s+of\s+the\s+year`
);
const RE_UNTIL = rx(String.raw`${UNTIL}\s+(?:and\s+including\s+)?(?:the\s+)?((?:${DATE}))`);
// "until Sunday". Not a bare "to": "Monday to Friday" is a run of days.
const RE_UNTIL_DAY = rx(
  String.raw`(?:valid\s+)?(?:until|till|til|through|thru|up\s+to|ending(?:\s+on)?|ends(?:\s+on)?|expires(?:\s+on)?)\s+(?:and\s+including\s+)?(?:the\s+)?(?:(this\s+coming|this|next|coming)\s+)?(${DAY_WORD})`
);
const RE_FOR = rx(
  String.raw`for\s+(?:the\s+next\s+)?(\d{1,2}|an?|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)\s+(weeks?|months?)`
);
const RE_FROM = rx(
  String.raw`(?:valid\s+)?(?:from|starting|starts|start|beginning|as\s+of)(?:\s+(?:from|on))?\s+(?:the\s+)?((?:${DATE})|today|tomorrow)`
);
const RE_DATE = rx(DATE);
const RE_RELATIVE = rx(
  String.raw`(?:the\s+)?day\s+after\s+tomorrow|today|tonight|tomorrow|tmrw|tmr`
);
const RE_RANGE = rx(
  String.raw`(?:from\s+)?(${CLOCK})\s*(?:-|to|till|until|thru|through)\s*(${CLOCK})|between\s+(${CLOCK})\s+and\s+(${CLOCK})`
);
const RE_PERIOD = rx(
  String.raw`(?:(every|each)\s+|in\s+the\s+|(this)\s+)?(mornings?|afternoons?|evenings?|nights?|overnight)`
);
const RE_ALL_DAY = rx(
  String.raw`all[\s\-]day(?:\s+long)?|(?:at\s+)?any\s*time|(?:the\s+)?whole\s+day`
);
const INTERVALS: Array<[RegExp, number]> = [
  [
    rx(
      String.raw`every\s+(?:other|second|2nd)(?!\s+day)(?:\s+week)?|every\s+(?:2|two)\s+weeks|every\s+fortnight|fortnightly|bi-?weekly`
    ),
    2,
  ],
  [rx(String.raw`every\s+(?:third|3rd)(?!\s+day)(?:\s+week)?|every\s+(?:3|three)\s+weeks`), 3],
  [rx(String.raw`every\s+(?:fourth|4th)(?!\s+day)(?:\s+week)?|every\s+(?:4|four)\s+weeks`), 4],
  [rx(String.raw`every\s+week|each\s+week|weekly|once\s+a\s+week`), 1],
];
// One weekend, pinned to its dates. "weekends" / "every weekend" are below.
const RE_WEEKEND = rx(
  String.raw`(?:(?:on|at|over|for|during)\s+)?(this\s+coming|this|next|coming)\s+week-?end`
);
const RE_DAY_WORDS = rx(
  String.raw`(?:(?:on|at|every|each)\s+)?(?:the\s+)?(?:week\s*days?|week-?ends?|daily|every\s*day|each\s+day|(?:7|seven)\s+days\s+a\s+week)`
);
const DAY_SEP = String.raw`(?:\s*[,&+/]\s*(?:and\s+)?|\s+(?:and|or|plus)\s+|\s*-\s*|\s+(?:to|through|thru|till|until)\s+)`;
const RE_DAYS = rx(
  String.raw`(?:(?:every|each|on|this\s+coming|this|next|coming)\s+)?(?:the\s+)?(?:${DAY_WORD})(?:${DAY_SEP}(?:${DAY_WORD}))*`
);
const RE_CATEGORY = rx(
  String.raw`(?:${CATEGORY_WORDS.flatMap(([, words]) => words)
    .sort((a, b) => b.length - a.length)
    .map(word => word.replace(/ /g, String.raw`[\s\-]*`))
    .join('|')})(?:['’]s)?`
);

const TOKEN = /[\p{L}\p{N}]+(?:['’.:/-][\p{L}\p{N}]+)*/gu;
const NAME_WORD = /^\p{Lu}\p{L}*(?:['’-]\p{L}+)*$/u;
const DAY_ONLY = new RegExp(`^(?:${DAY_WORD})$`, 'i');
const NOT_NAME = new RegExp(`^(?:${DAY_WORD}|${MONTH})$`, 'i');
const DASHES = /[‐-―−⸺⸻﹘﹣－]/g;
const MASK = '￿';

/* ────────────────────────────  The workbench  ─────────────────────────── */

type DayMode = 'this' | 'next' | null;

interface Claim {
  start: number;
  end: number;
  /** Worked out from a span another field already quotes ("this weekend" is
   *  the days AND their two dates): fills its field, quoted and handed back by
   *  nobody. */
  derived?: boolean;
}

interface Hit extends Claim {
  core: string;
  groups: string[];
}

interface DateClaim extends Claim {
  date: string;
}

interface EndClaim extends Claim {
  date?: string;
  n?: number;
  unit?: 'week' | 'month';
  /** "until Sunday": the first such day from the start on. */
  weekday?: number;
  mode?: DayMode;
}

interface DayGroup extends Claim {
  days: number[];
  cue: boolean;
  mode: DayMode;
}

interface CountClaim extends Claim {
  n: number;
}

interface Valued<V> extends Claim {
  value: V | null;
}

interface State {
  /** The capped input, as typed — what spans and leftovers quote. */
  src: string;
  /** The same length as src: dashes normalised, consumed text masked. */
  work: string;
  today: string | null;
  fields: Partial<PassSpecInput>;
  spans: QuickAddSpan[];
  extra: Array<{ start: number; text: string }>;
  ends: EndClaim[];
  froms: DateClaim[];
  dates: DateClaim[];
  groups: DayGroup[];
  intervals: CountClaim[];
  weekly: Claim[];
  windowSet: boolean;
  pluralPeriod: boolean;
  everyPeriod: boolean;
  /** Where each role or category word ended: a name straight after one is the
   *  visitor ("my teacher Anna"). */
  roleEnds: number[];
  /** Where the first word of the text starts — the one a phone capitalises. */
  firstWord: number;
}

const pad = (n: number): string => String(n).padStart(2, '0');

const byStart = <C extends Claim>(list: C[]): C[] =>
  [...list].sort((a, b) => a.start - b.start);

const find = (s: State, re: RegExp): Hit[] => {
  const hits: Hit[] = [];
  re.lastIndex = 0;
  for (let m = re.exec(s.work); m; m = re.exec(s.work)) {
    const start = m.index + m[1].length;
    hits.push({
      start,
      end: start + m[2].length,
      core: m[2],
      groups: m.slice(3).map(g => g || ''),
    });
  }

  return hits;
};

const consume = (s: State, at: Claim): void => {
  s.work = s.work.slice(0, at.start) + MASK.repeat(at.end - at.start) + s.work.slice(at.end);
};

const claim = (s: State, at: Claim, field: QuickAddField): void => {
  s.spans.push({ field, start: at.start, end: at.end, text: s.src.slice(at.start, at.end) });
};

const reject = (s: State, at: Claim): void => {
  if (at.derived) return;
  s.extra.push({ start: at.start, text: s.src.slice(at.start, at.end).trim() });
};

/** The word just before `at`, when only white space separates them. */
const wordBefore = (work: string, at: number): string => {
  let end = at;
  while (end > 0 && /\s/.test(work[end - 1])) end -= 1;
  if (end === at) return '';
  let start = end;
  while (start > 0 && /[\p{L}'’-]/u.test(work[start - 1])) start -= 1;

  return work.slice(start, end);
};

/** The first value wins its field; an equal one joins it; a different one is a leftover. */
const firstWins = <V extends string | number>(
  s: State,
  claims: Array<Valued<V>>,
  field: QuickAddField
): V | null => {
  let winner: V | null = null;
  for (const c of claims) {
    if (c.value !== null && (winner === null || c.value === winner)) {
      winner = c.value;
      if (!c.derived) claim(s, c, field);
    } else {
      reject(s, c);
    }
  }

  return winner;
};

/* ────────────────────────────  Dates and days  ────────────────────────── */

const realDate = (year: number, month: number, day: number): string | null => {
  const date = `${String(year).padStart(4, '0')}-${pad(month)}-${pad(day)}`;

  return isRealDate(date) ? date : null;
};

/** A date said without a year: this year's, or next year's once this year's has gone. */
const comingDate = (month: number, day: number, today: string): string | null => {
  const year = Number(today.slice(0, 4));
  const thisYear = realDate(year, month, day);
  if (thisYear && thisYear >= today) return thisYear;

  return realDate(year + 1, month, day);
};

const monthNumber = (word: string): number => MONTH_KEYS.indexOf(word.slice(0, 3)) + 1;

/** A matched date as 'YYYY-MM-DD', or null when it is not one date (Feb 30, 3/4). */
const readDate = (raw: string, today: string): string | null => {
  const t = raw.toLowerCase().replace(/\s+/g, ' ').trim();
  const dated = (year: string | undefined, month: number, day: number): string | null => {
    if (!year) return comingDate(month, day, today);

    return realDate(year.length === 2 ? 2000 + Number(year) : Number(year), month, day);
  };

  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(t);
  if (m) return realDate(Number(m[1]), Number(m[2]), Number(m[3]));
  m = /^(?:the )?(\d{1,2})(?:st|nd|rd|th)? (?:of )?([a-z]+)\.?(?:,? (\d{4}))?$/.exec(t);
  if (m) return dated(m[3], monthNumber(m[2]), Number(m[1]));
  m = /^([a-z]+)\.? (?:the )?(\d{1,2})(?:st|nd|rd|th)?(?:,? (\d{4}))?$/.exec(t);
  if (m) return dated(m[3], monthNumber(m[1]), Number(m[2]));
  m = /^(\d{1,2})\/(\d{1,2})(?:\/(\d{4}|\d{2}))?$/.exec(t);
  if (m) {
    const a = Number(m[1]);
    const b = Number(m[2]);
    // Only when one part cannot be a month: that part is the day.
    if (a > 12 && b >= 1 && b <= 12) return dated(m[3], b, a);
    if (b > 12 && a >= 1 && a <= 12) return dated(m[3], a, b);
  }

  return null;
};

/** ISO weekday of a calendar date; 1 Jan 2024 was a Monday. */
const weekdayOf = (date: string): number =>
  (((daysBetween('2024-01-01', date) % 7) + 7) % 7) + 1;

/** Nearest `day` from today on (today counts); 'next' = the first one AFTER today. */
const weekdayDate = (day: number, mode: DayMode, today: string): string => {
  const ahead = (day - weekdayOf(today) + 7) % 7;

  return addDays(today, mode === 'next' && ahead === 0 ? 7 : ahead);
};

/** The LAST day an end names: valid_until is inclusive (pass-schedule). */
const endOf = (c: EndClaim, from: string | null): string | null => {
  if (c.date) return c.date;
  if (!from) return null;
  if (c.weekday) return weekdayDate(c.weekday, c.mode ?? null, from);
  if (!c.n) return null;

  // "for 6 weeks" from a Tuesday ends on the Monday six weeks on: six Tuesdays.
  return addDays(c.unit === 'month' ? addMonths(from, c.n) : addDays(from, c.n * 7), -1);
};

const numberOf = (word: string): number => {
  const words = [
    'one',
    'two',
    'three',
    'four',
    'five',
    'six',
    'seven',
    'eight',
    'nine',
    'ten',
    'eleven',
    'twelve',
  ];
  const lower = word.toLowerCase();
  if (lower === 'a' || lower === 'an') return 1;
  const at = words.indexOf(lower);

  return at >= 0 ? at + 1 : Number(word);
};

const dayNumberOf = (word: string): number =>
  DAY_ONLY.test(word) ? DAY_KEYS.indexOf(word.slice(0, 3).toLowerCase()) + 1 : 0;

const RANGE_WORDS = new Set(['-', 'to', 'through', 'thru', 'till', 'until']);

/** "every Mon-Wed and Fri" -> [1, 2, 3, 5], a cue, no pinned date. */
const readDayGroup = (core: string): Omit<DayGroup, 'start' | 'end'> => {
  const words = core.toLowerCase().match(/[a-z]+|-/g) || [];
  const days: number[] = [];
  let lead: string | null = null;
  let ranging = false;
  let ranged = false;
  let plural = false;
  for (const word of words) {
    const day = dayNumberOf(word);
    if (day) {
      const prev = days[days.length - 1];
      if (ranging && prev) {
        let d = prev;
        while (d !== day) {
          d = (d % 7) + 1;
          days.push(d);
        }
        ranged = true;
      } else {
        days.push(day);
      }
      ranging = false;
      if (word.endsWith('days')) plural = true;
    } else if (RANGE_WORDS.has(word)) {
      ranging = days.length > 0;
    } else if (!days.length && lead === null) {
      lead = word;
    }
  }
  const unique = Array.from(new Set(days)).sort((a, b) => a - b);
  const cue = lead === 'every' || lead === 'each' || plural || ranged || unique.length > 1;
  let mode: DayMode = null;
  if (!cue && lead === 'next') mode = 'next';
  else if (!cue && (lead === 'this' || lead === 'coming')) mode = 'this';

  return { days: unique, cue, mode };
};

/* ─────────────────────────────────  Times  ────────────────────────────── */

interface Clock {
  h: number;
  m: number;
  mer: 'a' | 'p' | null;
  /** Written so it cannot mean anything else: am/pm, 24-hour, a word, "09". */
  fixed: boolean;
}

const readClock = (raw: string): Clock | null => {
  const t = raw.toLowerCase().replace(/\s+/g, '');
  if (t === 'noon' || t === 'midday') return { h: 12, m: 0, mer: null, fixed: true };
  if (t === 'midnight') return { h: 0, m: 0, mer: null, fixed: true };
  const m = /^(\d{1,2})(?:[:.](\d{2}))?(?:([ap])\.?m\.?)?$/.exec(t);
  if (!m) return null;
  let h = Number(m[1]);
  const min = m[2] ? Number(m[2]) : 0;
  const mer = m[3] === 'a' || m[3] === 'p' ? m[3] : null;
  if (min > 59) return null;
  if (mer) {
    if (h < 1 || h > 12) return null;
    h = (h % 12) + (mer === 'p' ? 12 : 0);
  } else {
    if (h > 24 || (h === 24 && min > 0)) return null;
    if (h === 24) h = 0;
  }
  const zeroPadded = m[1].length === 2 && m[1][0] === '0';

  return { h, m: min, mer, fixed: mer !== null || h > 12 || h === 0 || zeroPadded };
};

/** Two clock readings as a window, bare hours read as the header says. */
const clockRange = (a: Clock, b: Clock): [string, string] => {
  let start = a.h;
  let end = b.h;
  // "1-3pm": the start takes the end's pm when that keeps it before the end.
  if (a.mer === null && !a.fixed && b.mer !== null) {
    const shifted = (start % 12) + (b.mer === 'p' ? 12 : 0);
    if (shifted * 60 + a.m < end * 60 + b.m) start = shifted;
  }
  if (a.mer === 'p' && b.mer === null && !b.fixed) {
    // "6pm-10": a pm start lends its pm to a bare end that is later the same
    // evening. "9pm-12" ends at midnight; "10pm-2" is the next morning.
    const evening = (end % 12) + 12;
    if (evening * 60 + b.m > start * 60 + a.m) end = evening;
    else if (end === 12) end = 0;
  } else if (
    !a.fixed &&
    !b.fixed &&
    a.mer === null &&
    start >= 1 &&
    start <= 5 &&
    start * 60 + a.m < end * 60 + b.m &&
    end < 12
  ) {
    // "2-5", "4-4:30": an afternoon, not the small hours.
    start += 12;
    end += 12;
  } else if (!b.fixed && start <= 12 && end * 60 + b.m < start * 60 + a.m) {
    // "9-1", "12-1": the end is after noon.
    end += 12;
  }

  return [`${pad(start)}:${pad(a.m)}`, `${pad(end)}:${pad(b.m)}`];
};

/** An explicit range fills the window; a word (`soft`) only when nothing has. */
const setWindow = (
  s: State,
  hit: Claim,
  window: [string | null, string | null],
  soft: boolean
): void => {
  consume(s, hit);
  if (!s.windowSet) {
    s.fields.window_start = window[0];
    s.fields.window_end = window[1];
    s.windowSet = true;
    claim(s, hit, 'window');
  } else if (
    soft ||
    (s.fields.window_start === window[0] && s.fields.window_end === window[1])
  ) {
    claim(s, hit, 'window');
  } else {
    reject(s, hit);
  }
};

/* ─────────────────────────────────  Rules  ────────────────────────────── */

const readValidity = (s: State, today: string): void => {
  find(s, RE_END_OF_YEAR).forEach(hit => {
    consume(s, hit);
    s.ends.push({ start: hit.start, end: hit.end, date: `${today.slice(0, 4)}-12-31` });
  });
  find(s, RE_UNTIL).forEach(hit => {
    const date = readDate(hit.groups[0], today);
    if (!date) return;
    consume(s, hit);
    s.ends.push({ start: hit.start, end: hit.end, date });
  });
  find(s, RE_UNTIL_DAY).forEach(hit => {
    // "Monday until Friday" is a run of days, read whole by the day rule;
    // "Tuesdays until Friday" is Tuesdays, ending on Friday.
    const before = wordBefore(s.work, hit.start);
    if (DAY_ONLY.test(before) && !/days$/i.test(before)) return;
    const [lead, word] = hit.groups;
    const weekday = dayNumberOf(word);
    if (!weekday) return;
    // Consumed here, so the day rule never reads the end as a visiting day.
    consume(s, hit);
    s.ends.push({
      start: hit.start,
      end: hit.end,
      weekday,
      mode: /next/i.test(lead) ? 'next' : null,
    });
  });
  find(s, RE_FOR).forEach(hit => {
    const n = numberOf(hit.groups[0]);
    const unit = /^w/i.test(hit.groups[1]) ? 'week' : 'month';
    if (!(n >= 1 && n <= (unit === 'week' ? 52 : 24))) return;
    consume(s, hit);
    s.ends.push({ start: hit.start, end: hit.end, n, unit });
  });
  find(s, RE_FROM).forEach(hit => {
    const said = hit.groups[0].toLowerCase();
    let date: string | null;
    if (said === 'today') date = today;
    else if (said === 'tomorrow') date = addDays(today, 1);
    else date = readDate(said, today);
    if (!date) return;
    consume(s, hit);
    s.froms.push({ start: hit.start, end: hit.end, date });
  });
};

const readDates = (s: State, today: string): void => {
  find(s, RE_DATE).forEach(hit => {
    const date = readDate(hit.core, today);
    if (!date) return;
    consume(s, hit);
    s.dates.push({ start: hit.start, end: hit.end, date });
  });
  find(s, RE_RELATIVE).forEach(hit => {
    const said = hit.core.toLowerCase();
    let ahead = 0;
    if (said.includes('after')) ahead = 2;
    else if (said.startsWith('tom') || said.startsWith('tm')) ahead = 1;
    consume(s, hit);
    s.dates.push({ start: hit.start, end: hit.end, date: addDays(today, ahead) });
  });
};

const readTimes = (s: State): void => {
  find(s, RE_RANGE).forEach(hit => {
    const [a1, b1, a2, b2] = hit.groups;
    const a = readClock(a1 || a2);
    const b = readClock(b1 || b2);
    if (!a || !b) return;
    setWindow(s, hit, clockRange(a, b), false);
  });
  find(s, RE_PERIOD).forEach(hit => {
    const [every, thisOne, word] = hit.groups;
    const lower = word.toLowerCase();
    if (every) s.everyPeriod = true;
    if (lower.endsWith('s')) s.pluralPeriod = true;
    // "this afternoon" is today's.
    if (thisOne && s.today) {
      s.dates.push({ start: hit.start, end: hit.end, date: s.today, derived: true });
    }
    setWindow(s, hit, PERIODS[lower[0]], true);
  });
  find(s, RE_ALL_DAY).forEach(hit => setWindow(s, hit, [null, null], true));
};

const readRepeats = (s: State): void => {
  INTERVALS.forEach(([re, n]) => {
    find(s, re).forEach(hit => {
      consume(s, hit);
      if (n > 1) s.intervals.push({ start: hit.start, end: hit.end, n });
      else s.weekly.push({ start: hit.start, end: hit.end });
    });
  });
};

const readDays = (s: State): void => {
  const { today } = s;
  if (today) {
    find(s, RE_WEEKEND).forEach(hit => {
      // Weeks start on Monday, as the backend reads them (src/iq/named-day.js):
      // "this weekend" is this week's Saturday and Sunday — from today on, when
      // it is already Sunday — and "next weekend" is the one after.
      const monday = addDays(today, 1 - weekdayOf(today));
      const saturday = addDays(monday, /next/i.test(hit.groups[0]) ? 12 : 5);
      consume(s, hit);
      s.groups.push({ start: hit.start, end: hit.end, days: [6, 7], cue: true, mode: null });
      s.froms.push({
        start: hit.start,
        end: hit.end,
        date: saturday < today ? today : saturday,
        derived: true,
      });
      s.ends.push({ start: hit.start, end: hit.end, date: addDays(saturday, 1), derived: true });
    });
  }
  find(s, RE_DAY_WORDS).forEach(hit => {
    const said = hit.core.toLowerCase();
    let days = [1, 2, 3, 4, 5];
    if (/daily|every\s*day|each\s+day|days\s+a\s+week/.test(said)) days = EVERY_DAY;
    else if (said.includes('end')) days = [6, 7];
    consume(s, hit);
    s.groups.push({ start: hit.start, end: hit.end, days, cue: true, mode: null });
  });
  find(s, RE_DAYS).forEach(hit => {
    consume(s, hit);
    s.groups.push({ start: hit.start, end: hit.end, ...readDayGroup(hit.core) });
  });
};

const withoutPossessive = (word: string): string =>
  word.length > 3 ? word.replace(/['’]s$/, '') : word;

const isNameWord = (word: string): boolean => {
  const bare = withoutPossessive(word);
  const lower = bare.toLowerCase();

  return (
    bare.length <= 50 &&
    NAME_WORD.test(bare) &&
    !FILLER.has(lower) &&
    !KEYWORDS.has(lower) &&
    !NOT_A_PERSON.has(lower) &&
    (!NOT_NAME.test(bare) || NAME_MONTHS.has(lower))
  );
};

type Token = Claim & { text: string };

const tokensOf = (work: string): Token[] => {
  const out: Token[] = [];
  TOKEN.lastIndex = 0;
  for (let m = TOKEN.exec(work); m; m = TOKEN.exec(work)) {
    out.push({ start: m.index, end: m.index + m[0].length, text: m[0] });
  }

  return out;
};

/** "piano" of "piano teacher": the plain word a subject role is said with. */
const subjectBefore = (s: State, at: number): Token | null => {
  const before = tokensOf(s.work)
    .filter(token => token.end <= at)
    .pop();
  if (!before || !/^\s+$/.test(s.work.slice(before.end, at))) return null;
  const lower = before.text.toLowerCase();
  if (!/^\p{L}+$/u.test(before.text) || FILLER.has(lower) || KEYWORDS.has(lower)) return null;

  return before;
};

interface RoleHit extends Claim {
  category: PassCategory;
  /** "mum's": whose visitor it is, when another kind of visitor follows. */
  possessive: boolean;
}

const readCategory = (s: State): void => {
  const hits: RoleHit[] = [];
  find(s, RE_CATEGORY).forEach(hit => {
    const key = hit.core
      .toLowerCase()
      .replace(/['’]s$/, '')
      .replace(/[\s-]+/g, '');
    const category = CATEGORY_OF.get(key);
    if (!category) return;
    // "Sam Walker": a capitalised job word straight after a name is a surname.
    const oneWord = !/[\s-]/.test(hit.core);
    if (
      oneWord &&
      !SUBJECT_ROLES.has(key) &&
      /^\p{Lu}/u.test(hit.core) &&
      isNameWord(wordBefore(s.work, hit.start))
    ) {
      return;
    }
    consume(s, hit);
    const role: RoleHit = {
      start: hit.start,
      end: hit.end,
      category,
      possessive: /['’]s$/.test(hit.core),
    };
    // "Piano teacher": the subject is part of the role, never a name.
    const subject = SUBJECT_ROLES.has(key) ? subjectBefore(s, hit.start) : null;
    if (subject) {
      consume(s, subject);
      role.start = subject.start;
    }
    hits.push(role);
    s.roleEnds.push(role.end);
  });

  hits.forEach((hit, at) => {
    // "My mum's carer": the carer is the visitor; "mum's" says whose.
    if (hit.possessive && hits.slice(at + 1).some(later => later.category !== hit.category)) {
      return;
    }
    if (s.fields.category === undefined) {
      s.fields.category = hit.category;
      claim(s, hit, 'category');
    } else if (s.fields.category === hit.category) {
      claim(s, hit, 'category');
    } else {
      reject(s, hit);
    }
  });
};

const readName = (s: State): void => {
  const tokens = tokensOf(s.work);
  const adjacent = (a: Token, b: Token): boolean => /^\s+$/.test(s.work.slice(a.end, b.start));
  /* A Capitalised word that can be somebody's name here: not the word after
     "from" (a company, a place, a sender) and not the word in front of a thing
     ("Amazon delivery", "Birthday party"). */
  const canBeName = (i: number): boolean => {
    const token = tokens[i];
    if (!isNameWord(token.text)) return false;
    if (wordBefore(s.work, token.start).toLowerCase() === 'from') return false;
    const next = tokens[i + 1];

    return !(next && adjacent(token, next) && NOT_A_PERSON.has(next.text.toLowerCase()));
  };
  /* Capitalised by the keyboard, not by the writer: the first word of the text,
     and the first word after a full stop. It proves nothing by itself. */
  const startsSentence = (token: Token): boolean =>
    token.start === s.firstWord || /[.!?]["')\]]*\s+$/.test(s.src.slice(0, token.start));
  const afterRole = (token: Token): boolean =>
    s.roleEnds.some(end => end < token.start && /^\s+$/.test(s.work.slice(end, token.start)));
  const runFrom = (at: number): Token[] => {
    const run = [tokens[at]];
    for (let i = at + 1; i < tokens.length && run.length < 3; i += 1) {
      if (!adjacent(run[run.length - 1], tokens[i]) || !isNameWord(tokens[i].text)) break;
      run.push(tokens[i]);
    }

    return run;
  };

  const names = tokens.map((_, i) => i).filter(canBeName);
  if (!names.length) return;
  let at = names[0];
  if (startsSentence(tokens[at])) {
    const last = at + runFrom(at).length - 1;
    const later = names.filter(i => i > last && !startsSentence(tokens[i]));
    if (later.length) {
      // Two words could be the visitor. One straight after a role is; with no
      // such sign neither is filled in — both come back as not understood.
      const named = later.find(i => afterRole(tokens[i]));
      if (named === undefined) return;
      at = named;
    }
  }
  const run = runFrom(at);
  s.fields.first_name = withoutPossessive(run[0].text);
  claim(s, run[0], 'first_name');
  if (run.length > 1) {
    s.fields.last_name = run
      .slice(1)
      .map(token => withoutPossessive(token.text))
      .join(' ');
    claim(s, { start: run[1].start, end: run[run.length - 1].end }, 'last_name');
  }
  run.forEach(token => consume(s, token));
};

/** Once or weekly, and the dates that follow from it — see the header. */
const settle = (s: State): void => {
  const { fields, today } = s;
  const plain = s.groups.filter(g => !g.cue);
  const distinct = Array.from(new Set(s.groups.flatMap(g => g.days))).sort((a, b) => a - b);
  const pinned = plain.some(g => g.mode !== null);
  // "staying until Sunday": an end named by its weekday and no visiting day is
  // a stay — every day from the start until then.
  const stay = s.groups.length === 0 && s.ends.some(c => c.weekday !== undefined);
  const repeats =
    s.groups.some(g => g.cue) ||
    distinct.length > 1 ||
    s.intervals.length > 0 ||
    s.weekly.length > 0 ||
    s.everyPeriod ||
    stay ||
    (s.pluralPeriod && s.groups.length > 0) ||
    (plain.length > 0 && s.ends.length > 0 && s.dates.length === 0);

  let kind: PassScheduleKind | null = null;
  if (pinned) kind = 'once';
  else if (repeats) kind = 'weekly';
  else if (plain.length > 0 || s.dates.length > 0) kind = 'once';

  if (kind === 'weekly') {
    fields.schedule_kind = 'weekly';
    s.weekly.forEach(c => claim(s, c, 'schedule_kind'));
    const days = distinct.length ? distinct : s.everyPeriod || stay ? [...EVERY_DAY] : [];
    if (days.length) fields.days = days;
    s.groups.forEach(g => claim(s, g, 'days'));
    const interval = firstWins(
      s,
      s.intervals.map(c => ({ ...c, value: c.n })),
      'interval_weeks'
    );
    if (interval !== null) fields.interval_weeks = interval;
    const starts = [...byStart(s.froms), ...byStart(s.dates)];
    let from =
      firstWins(
        s,
        starts.map(c => ({ ...c, value: c.date })),
        'valid_from'
      ) ?? today;
    // "Every other Tuesday" starts on the coming Tuesday (RP-D22): the weeks are
    // counted from the start's own week, so a start that is not a visiting day
    // would put the first visit up to four weeks away.
    if (from && interval !== null && interval > 1) from = firstDayOnOrAfter(from, days);
    if (from) fields.valid_from = from;
    const until = firstWins(
      s,
      byStart(s.ends).map(c => ({ ...c, value: endOf(c, from) })),
      'valid_until'
    );
    if (until) fields.valid_until = until;

    return;
  }

  if (kind === 'once') {
    const candidates = byStart<Valued<string>>([
      ...plain.map(g => ({
        start: g.start,
        end: g.end,
        value: today ? weekdayDate(g.days[0], g.mode, today) : null,
      })),
      ...s.dates.map(c => ({ ...c, value: c.date })),
      ...s.froms.map(c => ({ ...c, value: c.date })),
    ]);
    const date = firstWins(s, candidates, 'valid_from');
    if (date) {
      fields.schedule_kind = 'once';
      fields.valid_from = date;
    }
    // A single visit neither repeats nor ends: whatever said so is handed back.
    [...s.groups.filter(g => g.cue), ...s.intervals, ...s.weekly, ...s.ends].forEach(c =>
      reject(s, c)
    );

    return;
  }

  const from = firstWins(
    s,
    byStart(s.froms).map(c => ({ ...c, value: c.date })),
    'valid_from'
  );
  if (from) fields.valid_from = from;
  const until = firstWins(
    s,
    byStart(s.ends).map(c => ({ ...c, value: endOf(c, from ?? today) })),
    'valid_until'
  );
  if (until) fields.valid_until = until;
};

const leftoversOf = (s: State): string[] => {
  const found = s.extra.concat(
    tokensOf(s.work)
      .filter(token => !FILLER.has(token.text.toLowerCase()))
      .map(token => ({ start: token.start, text: s.src.slice(token.start, token.end) }))
  );
  found.sort((a, b) => a.start - b.start);
  const seen = new Set<string>();

  return found
    .map(f => f.text)
    .filter(text => {
      const key = text.toLowerCase();
      if (!text || seen.has(key)) return false;
      seen.add(key);

      return true;
    });
};

/** At most QUICK_ADD_MAX UTF-16 units, never ending on half a surrogate pair. */
const capText = (text: string): string => {
  if (text.length <= QUICK_ADD_MAX) return text;
  const cut = text.slice(0, QUICK_ADD_MAX);
  const last = cut.charCodeAt(cut.length - 1);

  return last >= 0xd800 && last <= 0xdbff ? cut.slice(0, -1) : cut;
};

/**
 * Read one sentence into pass-editor fields. Pure: the same text and `today`
 * ('YYYY-MM-DD', the building's date) always give the same result, and nothing
 * is saved — the result only pre-fills a form the user confirms.
 */
export const parseQuickAdd = (
  text: string,
  today: string,
  // Accepted, unused: `today` is already the building's date (see the header).
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  zone?: string
): QuickAddResult => {
  const src = capText(typeof text === 'string' ? text : '');
  const s: State = {
    src,
    work: src.replace(/￿/g, ' ').replace(DASHES, '-'),
    today: isRealDate(today) ? today : null,
    fields: {},
    spans: [],
    extra: [],
    ends: [],
    froms: [],
    dates: [],
    groups: [],
    intervals: [],
    weekly: [],
    windowSet: false,
    pluralPeriod: false,
    everyPeriod: false,
    roleEnds: [],
    firstWord: -1,
  };
  s.firstWord = tokensOf(s.work)[0]?.start ?? -1;

  // Without a real `today` no date can be worked out; those words stay leftovers.
  if (s.today) {
    readValidity(s, s.today);
    readDates(s, s.today);
  }
  readTimes(s);
  readRepeats(s);
  readDays(s);
  readCategory(s);
  readName(s);
  settle(s);

  return {
    fields: s.fields,
    matched: byStart(s.spans),
    leftovers: leftoversOf(s),
  };
};
